import { useEffect } from 'react';
import { loginRequest } from '../../config/msal';
import { BrowserAuthErrorCodes, InteractionStatus } from '@azure/msal-browser';
import { AuthenticatedTemplate, useMsal } from '@azure/msal-react';

import authStore from '../../store/auth';
import { schedulePreExpiryRefresh } from '../../config/api';
import { reportError } from '../../services/logger';

// Another MSAL caller already owns the redirect when interaction_in_progress is returned.
const startLoginRedirect = (instance, request) =>
  instance.loginRedirect(request).catch(redirectError => {
    if (redirectError?.errorCode === BrowserAuthErrorCodes.interactionInProgress) return;
    reportError('auth.loginRedirect', redirectError);
  });

function AuthenticatedProvider({ children }) {
  const { instance, inProgress, accounts } = useMsal();

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    const account = instance.getActiveAccount() ?? accounts[0];

    if (!account) {
      const currentUrl = encodeURIComponent(window.location.href);

      startLoginRedirect(instance, {
        ...loginRequest,
        state: currentUrl
      });
      return;
    }

    const accessTokenRequest = {
      ...loginRequest,
      account
    };

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then(accessTokenResponse => {
        instance.setActiveAccount(accessTokenResponse.account);
        authStore.authorize({ ...accessTokenResponse.account }, accessTokenResponse.accessToken);
        // Kick off the pre-expiry refresh loop off this token's own expiresOn
        // — every subsequent silent refresh reschedules itself, so a tab left
        // idle stays authenticated without waiting for a 401 to trigger it.
        schedulePreExpiryRefresh(accessTokenResponse);

        // Check if there is a "state" parameter in the URL
        const queryParams = new URLSearchParams(window.location.search);
        const originalUrl = queryParams.get('state');
        if (originalUrl) {
          // Navigate to the original URL
          window.location.replace(decodeURIComponent(originalUrl));
        }
      })
      .catch(error => {
        // If interaction is required, redirect to login
        const currentUrl = encodeURIComponent(window.location.href);

        reportError('auth.acquireTokenSilent', error);

        // Add the original URL to the "state" parameter
        startLoginRedirect(instance, {
          ...accessTokenRequest,
          state: currentUrl // Pass the current URL in the state parameter
        });
      });
  }, [instance, accounts, inProgress, authStore.token]);

  return <AuthenticatedTemplate>{children}</AuthenticatedTemplate>;
}

export default AuthenticatedProvider;
